import { InjectConfig, IqConfigKey } from '@ddt-web/config-layer';
import { CellData, ComponentData, TabData, TableData } from '@ddt-web/data-service';
import * as groupBy from 'lodash/groupBy';
import * as uniqBy from 'lodash/uniqBy';

import { statusChangeMapping } from '../constants/status-change-mapping';
import { TaxQualifiedAnalysisType } from '../enums';
import { StatusChange } from '../enums/status-change.enum';
import { FundData } from '../models/fund-data.interface';
import { MorningstarCategoryOrder } from '../models/morning-star-category-order.interface';
import { StatusColour } from '../models/status-colour.interface';

export class FundAnalysisDataTransformerUtil {
	@InjectConfig(IqConfigKey.MORNINGSTAR_CATEGORY_ORDER) private static readonly morningstarCategoryOrder: MorningstarCategoryOrder[] = [];

	private static readonly trueCell: CellData = CellData.create({
		formattedValue: 'true',
		rawValue: true,
		config: null,
		attribute: null
	});

	public static transformComponentData(
		componentData: ComponentData,
		statusesData?: ComponentData,
		tagsData?: ComponentData,
		portfoliosStructureData?: ComponentData,
		statuses?: string[],
		aof?: string,
		statusChange?: string,
		taxQualifiedAnalysisType?: string,
		interactionId?: string
	): FundData[][] {
		let transformedData = this.transformPositionData(componentData.getTabById('fund.analysis.table.position.details'));

		const aumData = portfoliosStructureData?.getTabById('fund.analysis.table.portfolio.aum');
		const weightData = portfoliosStructureData?.getTabById('fund.analysis.table.portfolio.weights');
		const portfoliosData = portfoliosStructureData?.getTabById('fund.analysis.table.portfolios');
		const taxData = portfoliosStructureData?.getTabById('fund.analysis.table.portfolios.tax.qualified.flag');
		const tagData = tagsData?.getTabById('fund.analysis.table.tags');
		const statusData = statusesData?.getTabById('fund.analysis.table.statuses') || componentData.getTabById('fund.analysis.table.statuses');

		transformedData = this.transformAndInsertOtherTabData(transformedData, [aumData, weightData, portfoliosData]);

		if (taxQualifiedAnalysisType === TaxQualifiedAnalysisType.MIXED || taxQualifiedAnalysisType === TaxQualifiedAnalysisType.ALL) {
			transformedData = this.enrichFundsWithTaxAttribute(transformedData, taxData);
		}

		transformedData = this.transformAndInsertGenericFundData(transformedData, [statusData, tagData]);
		transformedData = this.enrichAndFilterFundsWithFundIdeaFlags(transformedData);
		transformedData = this.enrichAndFilterBuyFunds(transformedData);
		transformedData = this.enrichFundsWithStatusChange(transformedData);
		transformedData = this.enrichFundsWithStatusInteractions(transformedData);

		transformedData = this.filterTransformedData(transformedData, statusChange, statuses, aof, interactionId);

		return this.insertAndSortMorningstarCategoryRows(transformedData);
	}

	/**
	 * Updates only the tag data within the transformed table data of the table.
	 * It also applies the buy funds logic and the same morningstar categories rows and sorting.
	 *
	 * @param transformedData transformed data from the table to be updated
	 * @param tagsData tag component data
	 * @returns updated data with tags
	 */
	public static updateTableTagsData(transformedData: FundData[][], tagsData: ComponentData): FundData[][] {
		// remove first index row in each data rows collection (it represents the morningstar category name mock row needed for categories separation)
		const dataToUpdate = transformedData.map(data => data.filter((_, index) => index !== 0)).flat();

		// update rows with tags (where required)
		const dataUpdatedWithTags = FundAnalysisDataTransformerUtil.transformAndInsertGenericFundData(dataToUpdate, [tagsData.getTabById('fund.analysis.table.tags')]);

		// update funds with buy funds specific cells
		const dataUpdatedWithBuyFundsCells = FundAnalysisDataTransformerUtil.enrichAndFilterBuyFunds(dataUpdatedWithTags);

		// add morningstar categories rows back + sorting + filter data setup - needed for tags updates
		const updatedTransformedData = FundAnalysisDataTransformerUtil.insertMorningstarCategoryRows(dataUpdatedWithBuyFundsCells);
		return FundAnalysisDataTransformerUtil.sortMorningstarCategories(updatedTransformedData);
	}

	public static updateTableStatusData(
		transformedData: FundData[][],
		statusData: ComponentData,
		statuses: string[],
		aof: string,
		statusChange: string,
		interactionId: string
	): FundData[][] {
		// remove first index row in each data rows collection (it represents the morningstar category name mock row needed for categories separation)
		let updatedTransformedData: FundData[] = transformedData.map(data => data.filter((_, index) => index !== 0)).flat();

		// update rows with status interaction cells
		updatedTransformedData = FundAnalysisDataTransformerUtil.transformAndInsertGenericFundData(
			updatedTransformedData,
			[statusData.getTabById('fund.analysis.table.statuses')],
			true
		);

		// apply cells as attributes to the status
		updatedTransformedData = FundAnalysisDataTransformerUtil.enrichFundsWithStatusChange(updatedTransformedData);
		updatedTransformedData = FundAnalysisDataTransformerUtil.enrichFundsWithStatusInteractions(updatedTransformedData);

		// update funds with buy funds specific cells
		updatedTransformedData = FundAnalysisDataTransformerUtil.enrichAndFilterBuyFunds(updatedTransformedData);

		updatedTransformedData = this.filterTransformedData(updatedTransformedData, statusChange, statuses, aof, interactionId);

		return this.insertAndSortMorningstarCategoryRows(updatedTransformedData);
	}

	// The tabs coming from the associated portfolios store come in a different format and need to be transformed differently, and the values need to be aggregated
	public static transformPortfolioData(tabData: TabData): FundData[] {
		const tableData: TableData[] = tabData?.tableData ?? [];

		return [].concat(...tableData.map(data => {
			return data.cells.map((row: CellData[]) => {
				const fund: FundData = {};
				data.headersX.forEach((header: CellData, index: number) => {
					let key: string = header.config.id;

					if (key !== 'name') {
						fund[key] = { ...row[index] };
					} else {
						key = row[index]?.config?.id as any as string;
						if (!!fund[key]) {
							const newValue = Number(fund[key].rawValue) + Number(row[index].rawValue);
							fund[key] = {
								...fund[key],
								rawValue: newValue,
								formattedValue: fund[key].config?.labelFormatter?.format(newValue)?.toString()
							};
						} else {
							fund[key] = { ...row[index] };
						}
						let contributors: string[] = [...(fund[key].attribute?.contributors || [])];
						if (!!row[index].rawValue) {
							contributors = [...contributors, header.rawValue as any as string];
						}
						fund[key].attribute = {
							...(fund[key].attribute ?? {}),
							contributors
						};
					}
				});

				return fund;
			});
		}));
	}

	private static transformPositionData(tabData: TabData): FundData[] {
		const tableData: TableData[] = tabData?.tableData ?? [];

		return [].concat(...tableData.map(data => {
			const cells: CellData[][] = data.cells;

			return cells.map((row: CellData[]) => {
				const fund: FundData = {};
				data.headersX.forEach((header: CellData, index: number) => {
					let key: string = header.config.id;
					if (key === 'name') {
						key = header.rawValue as any as string;
					}
					if (!!key) {
						fund[key] = row[index];
					}
				});
				return fund;
			});
		}));
	}

	private static transformGenericFundData(tabData: TabData): FundData[] {
		const tableData: TableData[] = tabData?.tableData ?? [];

		return [].concat(...tableData.map(data => {
			const dataPointIds: string[] = data.headersX.map(header => header.config.id);
			const cellsByDataPointPerPortfolio: Array<{ [dataPoint: string]: CellData[] }> = data.cells.map(tableCells => groupBy(tableCells, (cell: CellData) => cell.config.id));
			const dataPerPortfolio: FundData[][] = cellsByDataPointPerPortfolio.map((cellsByDataPoint: { [dataPoint: string]: CellData[] }) => {
				const fundDataArray = [];
				dataPointIds.forEach(id => {
					cellsByDataPoint?.[id]?.forEach((dataPoint, index) => {
						const fundData = fundDataArray[index] ?? {};
						fundData[id] = dataPoint;
						fundDataArray[index] = fundData;
					});
				});
				return fundDataArray;
			});
			const mergedFunds = uniqBy([].concat(...dataPerPortfolio), 'clientId');

			return mergedFunds;
		}));
	}

	private static transformAndInsertOtherTabData(transformedData: FundData[], tabData: TabData[]): FundData[] {
		let updatedData: FundData[] = transformedData;
		tabData.forEach(tabDatum => {
			if (tabDatum) {
				const portfolioDataRows = this.transformPortfolioData(tabDatum);
				updatedData = updatedData.map(fund => {
					return { ...fund, ...(portfolioDataRows.find(row => row.identifier.rawValue === fund['requestId']?.rawValue) ?? {}) };
				});
			}
		});
		return updatedData;
	}

	// The format for the tag and status data is a bit different as there is no portfolio-specific data to be recorded
	private static transformAndInsertGenericFundData(transformedData: FundData[], tabData: TabData[], preserveStatusAttributes: boolean = false): FundData[] {
		let updatedData: FundData[] = transformedData;
		tabData?.forEach(tabDatum => {
			const dataRows = this.transformGenericFundData(tabDatum);
			updatedData = updatedData.map(fund => {
				const updatedRow = { ...fund, ...(dataRows.find(row => row.requestId?.rawValue === fund['requestId']?.rawValue) ?? {}) };
				if (preserveStatusAttributes) {
					updatedRow.status.attribute = fund.status.attribute;
				}

				return updatedRow;
			});
		});
		return updatedData;
	}

	private static enrichFundsWithTaxAttribute(transformedData: FundData[], tabData: TabData): FundData[] {
		const updatedData: FundData[] = transformedData;

		if (tabData) {
			const tableData: TableData[] = tabData?.tableData ?? [];

			tableData?.[0]?.cells.map((row: CellData[]) => {
				const taxQualifiedFlagValues: CellData[] = row.filter(cell => cell?.config?.id === 'taxExemptedFlag' && cell?.rawValue !== '') ?? [];
				const isAnyNonTaxQualifiedPortfolioExists: boolean = taxQualifiedFlagValues.some(cell => cell?.rawValue === false);
				const isEveryPortfolioTaxQualified: boolean = !!taxQualifiedFlagValues.length && taxQualifiedFlagValues.every(cell => cell?.rawValue === true);
				const isTaxQualifiedFund: boolean = isAnyNonTaxQualifiedPortfolioExists ? false : isEveryPortfolioTaxQualified;
				const identifier: string = row.find(x => x.config.id === 'identifier')?.rawValue?.toString();
				const fund: FundData = updatedData.find(data => data['requestId'].formattedValue === identifier);
				const taxOffenderValue = fund?.highTaxOffenders?.rawValue || fund?.worstTaxOffenders?.rawValue;

				if (taxOffenderValue) {
					fund.highTaxOffenders = this.insertAttribute(
						fund.highTaxOffenders,
						{
							isTaxQualifiedFund
						}
					);

					fund.worstTaxOffenders = this.insertAttribute(
						fund.worstTaxOffenders,
						{
							isTaxQualifiedFund
						}
					);
				}
			});
		}
		return updatedData;
	}

	private static enrichAndFilterFundsWithFundIdeaFlags(funds: FundData[]): FundData[] {
		const duplicateFunds: CellData[] = [];
		return funds.map(fund => {
			if (fund?.TRANSIENT_BLK_IDEAS?.rawValue || fund?.TRANSIENT_BUY_FUNDS_BLK_IDEAS?.rawValue) {
				const isRedStatusFundIdea = fund?.status?.rawValue === StatusColour.RED.colour;
				const transientBookFundIdea: FundData = funds
					.filter(fund => fund?.TRANSIENT_BOOK?.rawValue)
					.find(transientBookFund => transientBookFund.morningstarSecId?.rawValue === fund.morningstarSecId.rawValue);

				// The requestId for fundIdeas is GROUP-morningstarSecId - we need to take the prefix to get the group
				const ideaGroup = fund.requestId?.formattedValue?.split('-')[0];
				if (!transientBookFundIdea) {
					if (isRedStatusFundIdea) {
						fund = null;
					} else {
						fund.status = this.insertAttribute(fund.status, {
							fundIdea: {
								isFundIdea: true,
								isSuggestedAddition: true,
								group: ideaGroup
							}
						});
					}
				} else {
					if (!isRedStatusFundIdea) {
						transientBookFundIdea.status = this.insertAttribute(fund.status, {
							fundIdea: {
								isFundIdea: true,
								group: ideaGroup
							}
						});
					}
					duplicateFunds.push(fund.requestId);
				}
			}
			return fund;

		}).filter(fund => !!fund && !duplicateFunds.includes(fund.requestId));
	}

	private static enrichFundsWithStatusChange(funds: FundData[]): FundData[] {
		return funds.map(fund => {
			const statuses: string[] = Object.keys(StatusColour.get);
			const currentStatus = <string>fund.status?.rawValue;
			const previousStatus = <string>fund.comparisonStatus?.rawValue;

			fund.status = this.insertAttribute(
				fund.status,
				{
					asOfDate: fund.date?.formattedValue
				}
			);

			if (!previousStatus) {
				return fund;
			}

			if (!fund.status.attribute.fundIdea?.isSuggestedAddition) {
				fund.status = this.insertAttribute(
					fund.status,
					{
						comparisonStatus: previousStatus,
						asOfDateCompare: fund.comparisonDate?.formattedValue
					}
				);

				if (statuses.indexOf(currentStatus) > statuses.indexOf(previousStatus)) {
					fund.status = this.insertAttribute(
						fund.status,
						{
							statusChange: StatusChange.UPGRADE
						}
					);
				} else if (statuses.indexOf(currentStatus) < statuses.indexOf(previousStatus)) {
					fund.status = this.insertAttribute(
						fund.status,
						{
							statusChange: StatusChange.DOWNGRADE
						}
					);
				} else {
					fund.status = this.insertAttribute(
						fund.status,
						{
							statusChange: StatusChange.NO_CHANGE
						}
					);
				}
			}

			return fund;
		});
	}

	private static enrichFundsWithStatusInteractions(funds: FundData[]): FundData[] {
		return funds.map(fund => {
			if (!fund.status.attribute?.fundIdea?.isSuggestedAddition) {
				fund.status = this.insertAttribute(
					fund.status,
					{
						statusInteraction: fund.statusInteraction?.rawValue
					}
				);
			}
			return fund;
		});
	}

	private static enrichAndFilterBuyFunds(funds: FundData[]): FundData[] {
		const duplicateFunds: CellData[] = [];
		const transientBookFunds = new Set<string>();
		const transientBlkIdeas = new Set<string>();
		const transientBuyFunds = new Set<string>();
		const transientBuyFundsBlkIdeas = new Set<string>();

		funds.forEach(fund => {
			const clientId = fund.clientId?.rawValue.toString();
			if (fund.TRANSIENT_BOOK?.rawValue) {
				transientBookFunds.add(clientId);
			}

			if (fund.TRANSIENT_BLK_IDEAS?.rawValue) {
				transientBlkIdeas.add(clientId);
			}

			if (fund.TRANSIENT_BUY_FUNDS?.rawValue) {
				transientBuyFunds.add(clientId);
			}

			if (fund.TRANSIENT_BUY_FUNDS_BLK_IDEAS?.rawValue) {
				transientBuyFundsBlkIdeas.add(clientId);
			}
		});

		// Logic to filter out the duplicate buy funds
		return funds.map(fund => {
			const clientId = fund.clientId?.rawValue.toString();

			if ((transientBuyFunds.has(clientId) && transientBlkIdeas.has(clientId))) {
				// a buy fund which is also a fund idea - keep the fund idea and remove the buy fund
				if (fund.TRANSIENT_BLK_IDEAS?.rawValue) {
					fund.status = this.insertAttribute(fund.status, {
						isBuyFund: true
					});
				} else {
					duplicateFunds.push(fund.requestId);
				}
			} else if ((transientBuyFunds.has(clientId) && transientBookFunds.has(clientId)) ||
				(transientBuyFundsBlkIdeas.has(clientId) && transientBookFunds.has(clientId))) {
				// a buy fund which is also in the analysis - remove the buy fund
				if (!fund.TRANSIENT_BOOK?.rawValue) {
					duplicateFunds.push(fund.requestId);
				}
			} else if (transientBuyFunds.has(clientId) && transientBuyFundsBlkIdeas.has(clientId)) {
				// a buy fund which is also a buy fund idea - remove the buy fund
				if (fund.TRANSIENT_BUY_FUNDS_BLK_IDEAS?.rawValue) {
					fund.status = this.insertAttribute(fund.status, {
						isBuyFund: true
					});
				} else {
					duplicateFunds.push(fund.requestId);
				}
			} else if (transientBuyFundsBlkIdeas.has(clientId) && Array.isArray(fund.tags?.rawValue)) {
				if (fund.tags.rawValue.includes('buy')) {
					// when a buy fund blk idea is tagged as a buy fund
					fund.status = this.insertAttribute(fund.status, {
						isBuyFund: true
					});
				} else {
					// and when it's no longer tagged as a buy fund
					fund.status = this.insertAttribute(fund.status, {
						isBuyFund: false
					});
				}
			} else if (transientBuyFunds.has(clientId)) {
				// a buy fund which is not in the analysis and it not a fund idea
				fund.status = this.insertAttribute(fund.status, {
					isBuyFund: true
				});
			}

			return fund;
		}).filter(fund => !duplicateFunds.includes(fund.requestId));
	}

	private static filterFundsByStatuses(funds: FundData[], statuses?: string[]): FundData[] {
		if (!statuses) {
			return [...funds];
		}
		const fundsWithStatus: FundData[] = funds.filter(fund => {
			const isDismissedFund: boolean = fund.status?.attribute?.statusInteraction === 'dismiss';
			return statuses.includes(fund.status?.rawValue.toString()) && !fund.status?.attribute?.fundIdea?.isSuggestedAddition && !isDismissedFund;
		});
		const morningstarCategoriesForFundsWithStatus: string[] = fundsWithStatus.map(fund => fund.morningstarCategory?.rawValue as unknown as string);

		return this.filterRelevantFundsForSelectedFilter(funds, fundsWithStatus, morningstarCategoriesForFundsWithStatus);
	}

	private static filterFundsByStatusInteraction(funds: FundData[], interactionId: string): FundData[] {
		if (!interactionId) {
			return [...funds];
		}

		const fundsWithStatusInteraction: FundData[] = funds.filter(fund => fund.statusInteraction?.rawValue === interactionId);
		const morningstarCategoriesForFundsWithStatusInteraction: string[] = fundsWithStatusInteraction.map(fund => fund.morningstarCategory?.rawValue as unknown as string);

		return this.filterRelevantFundsForSelectedFilter(funds, fundsWithStatusInteraction, morningstarCategoriesForFundsWithStatusInteraction);
	}

	private static filterFundsByStatusChange(funds: FundData[], statusChange?: StatusChange): FundData[] {
		if (!statusChange) {
			return [...funds];
		}

		const fundsWithStatusChange: FundData[] = funds.filter(fund => fund.status?.attribute?.statusChange === statusChange);
		const morningstarCategoriesForFundsWithStatusChange: string[] = fundsWithStatusChange.map(fund => fund.morningstarCategory?.rawValue as unknown as string);

		return this.filterRelevantFundsForSelectedFilter(funds, fundsWithStatusChange, morningstarCategoriesForFundsWithStatusChange);
	}

	private static filterFundsByAof(funds: FundData[], aof?: string): FundData[] {
		if (!aof) {
			return [...funds];
		}

		const fundsWithAof: FundData[] = funds.filter(fund => !!fund[aof]?.rawValue && !fund.status?.attribute?.fundIdea?.isSuggestedAddition);
		const morningstarCategoriesForFundsWithAof: string[] = fundsWithAof.map(fund => fund.morningstarCategory?.rawValue as unknown as string);

		return this.filterRelevantFundsForSelectedFilter(funds, fundsWithAof, morningstarCategoriesForFundsWithAof);
	}

	private static filterRelevantFundsForSelectedFilter(funds: FundData[], filteredFunds: FundData[], filteredFundsMorningstarCategories: string[]) {
		return funds.filter(fund => {
			const morningstarCategory: string = <string>fund.morningstarCategory?.rawValue;
			const isFundIdea: boolean = fund.status?.attribute?.fundIdea?.isFundIdea;

			return filteredFunds.includes(fund) ||

				// Or fund ideas in the morningstar categories containing flagged funds (both suggested additions and ideas already in the analysis)
				(filteredFundsMorningstarCategories.includes(morningstarCategory) && isFundIdea);
		});
	}

	private static sortMorningstarCategories(transformedData: FundData[][]): FundData[][] {
		const sortedFundData: FundData[][] = [...transformedData];

		sortedFundData.sort((category1: FundData[], category2: FundData[]) => {
			const morningstarCategory1Index: number = this.getMorningstarCategoryIndex(category1);
			const morningstarCategory2Index: number = this.getMorningstarCategoryIndex(category2);
			// Push all categories not in the Morningstar category order config all the way to the end of the table
			if (morningstarCategory1Index < 0) {
				return 1;
			} else if (morningstarCategory2Index < 0) {
				return -1;
			}
			return morningstarCategory1Index - morningstarCategory2Index;
		});

		return sortedFundData;
	}

	private static insertMorningstarCategoryRows(transformedData: FundData[]): FundData[][] {
		const fundsByMorningstarCategory: { [category: string]: FundData[] } = groupBy(transformedData, (fund) => fund['morningstarCategory'].rawValue);
		return Object.values(fundsByMorningstarCategory).map(categoryFunds => {
			const category = categoryFunds?.[0]?.morningstarCategory;
			return [
				{
					category,
					isMorningstarCategory: this.trueCell
				},
				...categoryFunds
			];
		});
	}

	private static getMorningstarCategoryIndex(categoryFundData: FundData[]): number {
		const morningstarCategoryData: FundData = categoryFundData.find(fundData => fundData.isMorningstarCategory?.rawValue);
		return this.morningstarCategoryOrder.findIndex(x => x.category === morningstarCategoryData.category?.formattedValue);
	}

	private static insertAttribute(cell: CellData, attributeToAdd: { [key: string]: any }): CellData {
		return {
			...cell,
			attribute: {
				...(cell?.attribute ?? {}),
				...attributeToAdd
			}
		};
	}

	private static filterTransformedData(transformedData: FundData[], statusChange: string, status: string[], aof: string, interactionId: string): FundData[] {
		if (statusChange) {
			transformedData = this.filterFundsByStatusChange(transformedData, statusChangeMapping[statusChange]);
		} else if (interactionId) {
			transformedData = this.filterFundsByStatusInteraction(transformedData, interactionId);
		} else {
			transformedData = this.filterFundsByStatuses(transformedData, status);
			transformedData = this.filterFundsByAof(transformedData, aof);
		}

		return transformedData;
	}

	private static insertAndSortMorningstarCategoryRows(transformedData: FundData[]) {
		const updatedTransformedDataWithMstarRows: FundData[][] = FundAnalysisDataTransformerUtil.insertMorningstarCategoryRows(transformedData);
		return FundAnalysisDataTransformerUtil.sortMorningstarCategories(updatedTransformedDataWithMstarRows);
	}
}
